import { LIMITS } from '../data/limits';
import type { Rule } from '../types';
import { defineRule, findingsFor, flag, pass } from './helpers';

/**
 * Profile-basics rules.
 *
 * These are the checks we cannot infer from pasted text, so the form asks about
 * them directly (see `ProfileChecklist` in `types.ts`). Every one of them is a
 * yes/no answer and a five-minute fix, which makes this section the fastest
 * source of points on the whole report — and the reason it is weighted as
 * heavily as it is for students.
 *
 * Two rules here are gated by audience mode: an Open-to-Work badge is right for
 * someone job hunting and wrong for someone quietly employed, and the same
 * applies in reverse to a hiring banner.
 */

const f = findingsFor('completeness');

export const completenessRules: readonly Rule[] = [
  defineRule({
    id: 'completeness.photo',
    section: 'completeness',
    weight: 3,
    evaluate(ctx) {
      if (ctx.profile.checklist.hasPhoto) return pass();

      return flag(
        0,
        f(
          'no-photo',
          'critical',
          'You have no profile photo',
          'A profile without a photo reads as abandoned or fake, and most recruiters skip them without a second thought. It is the single most visible gap on a profile and the cheapest one to close.',
          'Any clear, recent, well-lit head-and-shoulders photo works. Your face should fill most of the frame, the background should be plain, and a phone camera is completely fine — a professional shoot is not required.'
        )
      );
    },
  }),

  defineRule({
    id: 'completeness.banner',
    section: 'completeness',
    weight: 1,
    evaluate(ctx) {
      if (ctx.profile.checklist.hasBanner) return pass();

      return flag(
        0,
        f(
          'no-banner',
          'polish',
          'You are using the default grey banner',
          'The banner is the largest piece of visual space on your profile. Leaving it as the default grey tells a reader that you have not looked at your profile recently.',
          'Add anything relevant and uncluttered: your campus, your workspace, a screenshot of something you built, or a simple graphic with your specialisation. Canva has free LinkedIn banner templates at the right dimensions.'
        )
      );
    },
  }),

  defineRule({
    id: 'completeness.custom-url',
    section: 'completeness',
    weight: 2,
    evaluate(ctx) {
      if (ctx.profile.checklist.hasCustomUrl) return pass();

      return flag(
        0,
        f(
          'no-custom-url',
          'important',
          'Your profile URL still has random characters in it',
          'The default URL ends in a string of digits, which is unusable on a CV, in an email signature, or read aloud. A clean URL is also a small but real signal that you know the platform.',
          'Change it under Edit public profile & URL to linkedin.com/in/firstname-lastname. Do it once and put it on your CV.'
        )
      );
    },
  }),

  defineRule({
    id: 'completeness.location',
    section: 'completeness',
    weight: 2,
    evaluate(ctx) {
      if (ctx.profile.checklist.hasLocation) return pass();

      return flag(
        0,
        f(
          'no-location',
          'important',
          'Your location is not set',
          'Almost every recruiter search is filtered by location, and LinkedIn cannot include you in a filter you have not answered. This one field can remove you from the results entirely.',
          'Set the city or region you want to work in — not necessarily where you are right now. If you are open to relocating, set the target city and say so in your About.'
        )
      );
    },
  }),

  defineRule({
    id: 'completeness.featured',
    section: 'completeness',
    weight: 1,
    evaluate(ctx) {
      if (ctx.profile.checklist.hasFeatured) return pass();

      return flag(
        0,
        f(
          'no-featured',
          'polish',
          'Your Featured section is empty',
          'Featured is the only place on the profile where a reader can see your actual work rather than your description of it. For anyone without much formal experience, proof of work does more than another paragraph.',
          'Add two or three items: a GitHub repository, a portfolio site, a project write-up, a certificate, or a post you wrote. Turn the section on from "Add profile section → Recommended → Add featured".'
        )
      );
    },
  }),

  defineRule({
    id: 'completeness.recommendations',
    section: 'completeness',
    weight: 2,
    evaluate(ctx) {
      // Guard against a malformed value reaching the engine from the form.
      const count = Math.max(0, Math.floor(ctx.profile.checklist.recommendationCount || 0));
      const target = LIMITS.recommendations.minRecommended;
      if (count >= target) return pass();

      return flag(
        count / target,
        f(
          'few-recommendations',
          'important',
          count === 0
            ? 'You have no recommendations'
            : `You have ${count} recommendation — aim for at least ${target}`,
          'Recommendations are the only part of your profile written by someone other than you, which is exactly why readers trust them more than anything you wrote. They are also the section people most often skip.',
          'Ask two people who saw you work — a manager, a professor, a project teammate. Remind them of one specific thing you did, so they have something concrete to write about. Offering to write theirs first works.'
        )
      );
    },
  }),

  defineRule({
    id: 'completeness.open-to-work',
    section: 'completeness',
    weight: 2,
    // Only for people who are actually looking. Suggesting this to someone
    // quietly employed would be actively bad advice.
    appliesTo: ['student', 'jobSeeker'],
    evaluate(ctx) {
      if (ctx.profile.checklist.openToWork) return pass();

      return flag(
        0.2,
        f(
          'not-open-to-work',
          'important',
          'You have not told LinkedIn you are looking',
          'The "Open to work" setting feeds LinkedIn\'s recruiter product directly: candidates who set it appear in a filter that recruiters use heavily. Without it you are invisible to searches specifically looking for available people.',
          'Turn on "Open to work" and list the job titles and locations you want. You can share it with recruiters only, without the green photo frame, if you would rather it stay discreet.'
        )
      );
    },
  }),

  defineRule({
    id: 'completeness.hiring',
    section: 'completeness',
    weight: 2,
    appliesTo: ['recruiter'],
    evaluate(ctx) {
      if (ctx.profile.checklist.hiring) return pass();

      return flag(
        0.2,
        f(
          'not-hiring-badge',
          'important',
          'Your profile does not show that you are hiring',
          'Candidates check whether you actually hire before they spend effort on a message. The "Hiring" frame and an open role on your profile turn a cold outreach into an obvious one.',
          'Turn on the "Hiring" photo frame and link the roles you are recruiting for, so a candidate can see the job without having to ask.'
        )
      );
    },
  }),
];
